import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { useFormik } from 'formik';

import { Button, LoginForm as Form } from '@libs/components';
import { ClientPlatformEnum, JurisdictionsEnum } from '@libs/backend-common';
import { hashString } from '@libs/helpers';

import { JurisdictionRenderer, TwoFactorAuth } from 'src/components';
import { ResultFeedbackType, TwoFactorAuthType } from 'src/constants';
import {
  useJurisdictionDetector,
  useModal,
  useShowToast,
  useTranslation,
} from 'src/hooks';
import {
  bitnetLoginFormValidation,
  loginFormValidation,
  RegexValidators,
} from 'src/validations';
import { deleteUserDeviceToken, getUserDeviceTokenKey } from 'src/helpers';
import { setOnInvalidDevToken } from 'src/setup';

import { useLoginForm } from '../hooks/useLoginForm';
import {
  CustomTypography,
  ModalContent,
  RegistrationContainer,
} from '../styles';

const LoginForm = () => {
  const { t } = useTranslation();
  const { showToast } = useShowToast();

  const { Dialog, openModal } = useModal();

  const { currentJurisdiction } = useJurisdictionDetector();

  const {
    values,
    handleSubmit,
    errors,
    touched,
    isValid,
    setFieldTouched,
    setFieldValue,
    setFieldError,
  } = useFormik({
    validateOnChange: false,
    validateOnMount: false,
    validateOnBlur: false,
    initialValues: {
      email: '',
      password: '',
    },
    onSubmit: async (record) => {
      if (loading) return;

      const formatedEmailOrPhone = record.email.replace(
        RegexValidators.HAS_GEORGIAN_PHONE_PREFIX,
        ''
      );

      const hashedValue = await hashString(values.email);
      const fullKey = getUserDeviceTokenKey(hashedValue);
      const userDeviceToken = localStorage.getItem(fullKey) || undefined;

      setOnInvalidDevToken(() => {
        deleteUserDeviceToken(hashedValue);
        showToast({
          type: ResultFeedbackType.ERROR,
          text1: t('common_unexpected_error'),
        });
      });

      setEmail(formatedEmailOrPhone);

      handleLogin({
        variables: {
          record: {
            ...record,
            email: formatedEmailOrPhone,
            clientPlatform: ClientPlatformEnum.WEB,
            userDeviceToken: userDeviceToken,
          },
        },
      });
    },
    validationSchema:
      currentJurisdiction === JurisdictionsEnum.GE
        ? bitnetLoginFormValidation
        : loginFormValidation,
  });

  const {
    twoFaType,
    errorMessage,
    onTwoFaStepCompete,
    clearErrorMessage,
    loading,
    onTwoFaAuthFinish,
    handleLogin,
    setEmail,
  } = useLoginForm(openModal, values);

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFieldTouched(name);
    setFieldValue(name, value);
    setFieldError(name, '');
    clearErrorMessage();
  };
  const isButtonEnabled = !errorMessage;

  const headerTitle = useMemo(() => {
    if (twoFaType === TwoFactorAuthType.EMAIL) {
      return t('two_fa_email_title');
    } else if (twoFaType === TwoFactorAuthType.SMS) {
      return t('two_fa_sms_title');
    }
    return t('two_fa_google_title');
  }, [t, twoFaType]);

  const navigate = useNavigate();
  const { colors } = useTheme();

  const handleRegisterClick = () => {
    navigate('/register');
  };

  const emailInputPlaceholder = useMemo(() => {
    if (currentJurisdiction === JurisdictionsEnum.GE) {
      return t('login_phone_or_email_placeholder');
    } else {
      return t('login_placeholder');
    }
  }, [currentJurisdiction, t]);

  const emailInputLabel = useMemo(() => {
    if (currentJurisdiction === JurisdictionsEnum.GE) {
      return t('login_phone_or_email');
    } else {
      return t('login_email');
    }
  }, [currentJurisdiction, t]);

  return (
    <>
      <Form
        values={values}
        isValid={isValid}
        isSubmitting={loading}
        onSubmit={handleSubmit}
        formError={t(errorMessage)}
        onInputChange={onInputChange}
        isButtonEnabled={isButtonEnabled}
        buttonText={t('common_continue')}
        linkPath="/password-recovery"
        linkText={t('login_forgot_password')}
        emailLabel={emailInputLabel}
        emailPlaceholder={emailInputPlaceholder}
        emailInputError={touched.email ? t(errors.email) : ''}
        passwordLabel={t('login_password')}
        passwordPlaceholder={t('login_password')}
        passwordInputError={touched.password ? t(errors.password) : ''}
      />

      <JurisdictionRenderer acceptedJurisdictions={[JurisdictionsEnum.UA]}>
        <RegistrationContainer>
          <CustomTypography
            color={colors.text.default}
            type="bodyButton"
            fontFamily="primaryMedium"
          >
            {t('common_or')}
          </CustomTypography>
          <Button
            size="md"
            title={t('login_register_text')}
            onClick={handleRegisterClick}
            type="button"
            buttonType="secondary"
          />
        </RegistrationContainer>
      </JurisdictionRenderer>

      <Dialog leftSpace={0} title={headerTitle}>
        {twoFaType && (
          <ModalContent>
            <TwoFactorAuth
              type={twoFaType}
              onAuthFinish={onTwoFaAuthFinish}
              onComplete={onTwoFaStepCompete}
            />
          </ModalContent>
        )}
      </Dialog>
    </>
  );
};

export default LoginForm;
